import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Tab,
  Tabs
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { customerService, paymentService } from '../../services';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State for customer data
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);  // Initialize with empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Fetch customer data
  const fetchCustomerData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch customer details
      const customerData = await customerService.getCustomerById(id);
      setCustomer(customerData || null);
      
      // Fetch customer's payments
      const paymentsResponse = await paymentService.getPayments({
        filter: { customerId: id }
      });
      setPayments(paymentsResponse.payments || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  };
  
  // Load customer data on initial render
  useEffect(() => {
    fetchCustomerData();
  }, [id]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Format currency based on customer's country
  const getCurrencyByCountry = (country) => {
    if (!country) return 'USD';
    
    switch(country.toLowerCase()) {
      case 'canada':
        return 'CAD';
      case 'usa':
        return 'USD';
      default:
        return 'THB'; // Thailand currency for other countries by default
    }
  };
  
  // Format currency
  const formatCurrency = (amount, overrideCurrency = null) => {
    const currency = overrideCurrency || getCurrencyByCountry(customer?.country);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Container>
    );
  }
  
  if (!customer) {
    return (
      <Container maxWidth="lg">
        <Alert severity="info" sx={{ mt: 4 }}>
          Customer not found
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link to="/admin" style={{ textDecoration: 'none', color: 'inherit' }}>
          Dashboard
        </Link>
        <Link to="/admin/customers" style={{ textDecoration: 'none', color: 'inherit' }}>
          Customers
        </Link>
        <Typography color="text.primary">
          {`${customer.firstName} ${customer.lastName}`}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 2 }}
            onClick={() => navigate('/admin/customers')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {`${customer.firstName} ${customer.lastName}`}
          </Typography>
          <Chip 
            label={customer.hasPayment ? "Active" : "New"} 
            color={customer.hasPayment ? "success" : "primary"}
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/customers/${id}/edit`)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      {/* Customer details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          {/* Customer info card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List disablePadding>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Email" 
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {customer.email}
                      </Box>
                    } 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Phone" 
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {customer.phone || 'Not provided'}
                      </Box>
                    } 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Location" 
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {customer.country}{customer.region ? `, ${customer.region}` : ''}
                      </Box>
                    } 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Customer Since" 
                    secondary={formatDate(customer.createdAt)} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          
          {/* Payment summary card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List disablePadding>
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Total Payments" 
                    secondary={payments.length} 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Total Amount" 
                    secondary={formatCurrency(
                      payments.reduce((sum, payment) => sum + payment.amount, 0)
                    )} 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemText 
                    primary="Last Payment" 
                    secondary={
                      payments.length > 0 
                        ? formatDate(payments[0].createdAt)
                        : 'No payments'
                    } 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Paper sx={{ width: '100%' }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="customer tabs">
                <Tab label="Overview" id="customer-tab-0" />
                <Tab label="Payments" id="customer-tab-1" />
                <Tab label="Notes" id="customer-tab-2" />
              </Tabs>
            </Box>
            
            {/* Overview tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Customer Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        First Name
                      </Typography>
                      <Typography variant="body1">
                        {customer.firstName}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Last Name
                      </Typography>
                      <Typography variant="body1">
                        {customer.lastName}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {customer.email}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        {customer.phone || 'Not provided'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Country
                      </Typography>
                      <Typography variant="body1">
                        {customer.country}
                      </Typography>
                    </Grid>
                    
                    {customer.province && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Province
                        </Typography>
                        <Typography variant="body1">
                          {customer.province}
                        </Typography>
                      </Grid>
                    )}
                    
                    {customer.state && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          State
                        </Typography>
                        <Typography variant="body1">
                          {customer.state}
                        </Typography>
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {customer.address || 'Not provided'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        City
                      </Typography>
                      <Typography variant="body1">
                        {customer.city || 'Not provided'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Postal Code
                      </Typography>
                      <Typography variant="body1">
                        {customer.postalCode || 'Not provided'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Additional Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Created At
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(customer.createdAt)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(customer.updatedAt)}
                      </Typography>
                    </Grid>
                    
                    {customer.referralSource && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Referral Source
                        </Typography>
                        <Typography variant="body1">
                          {customer.referralSource}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Payments tab */}
            <TabPanel value={tabValue} index={1}>
              {payments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <PaymentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No payments found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This customer has not made any payments yet.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {payments.map((payment) => (
                    <React.Fragment key={payment._id}>
                      <ListItem
                        sx={{ px: 0 }}
                        secondaryAction={
                          <Tooltip title="View payment details">
                            <IconButton edge="end" onClick={() => navigate(`/admin/payments/${payment._id}`)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle1">
                                {formatCurrency(payment.amount, payment.currency)}
                              </Typography>
                              <Chip
                                label={payment.status}
                                color={
                                  payment.status === 'paid' ? 'success' :
                                  payment.status === 'pending' ? 'warning' : 'error'
                                }
                                size="small"
                                sx={{ ml: 2 }}
                              />
                            </Box>
                          }
                          secondary={
                            <React.Fragment>
                              <Typography variant="body2" component="span">
                                {payment.paymentMethod} • {formatDate(payment.createdAt)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Reference: {payment.referenceNumber || 'N/A'}
                              </Typography>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </TabPanel>
            
            {/* Notes tab (placeholder) */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Customer notes functionality will be implemented in a future update.
                </Typography>
              </Box>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CustomerDetail;